import React, { useMemo, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router'

import {
  usePRFileArtboards,
  usePRFileSections,
  usePan,
  useRootNodeAbsoluteExtent,
  useSetPan,
} from '@sketch-hq/sketch-web-renderer'
import { CameraSnapshot } from '@sketch/canvas'

import { useCanvasScope } from '../../scope'

import { AnnotationDotFragment } from '@sketch/gql-types'

import { AnnotationOverlay } from '../../../annotations/components'
import { usePlaceDraftAnnotation } from '../../../annotations/hooks'
import {
  ArtboardContainerBounds,
  ReferenceBounds,
} from '../../../annotations/types'

interface CenteredDotLocationState {
  isFromCommentTab?: boolean
}

interface AnnotationsOverlayProps {
  parentWrapper: React.RefObject<HTMLElement | null>
  activeAnnotationIdentifier?: string
  /**
   * The camera, kept by the canvas that renders this. Handed down rather than
   * followed again here: one `CameraRefSync` per canvas is one component
   * re-rendering on every frame of a drag instead of two.
   */
  cameraRef: React.RefObject<CameraSnapshot>
}

const throttleUpdatePopover = () => {
  window.requestAnimationFrame(() => {
    /**
     * This is a work-around to force popper to update
     * its position.
     *
     * Because popper has listeners for scroll and window
     * resize, we send this fake event when the camera is moving
     *
     * The other way would be using refs and would make the
     * logic complex
     */
    window?.dispatchEvent(new CustomEvent('scroll'))
  })
}

export const AnnotationsOverlay = (props: AnnotationsOverlayProps) => {
  // `otherProps` is spread onto the overlay below, so anything it does not
  // take has to be named here.
  const { activeAnnotationIdentifier, cameraRef, ...otherProps } = props

  const location = useLocation<CenteredDotLocationState | undefined>()
  const artboards = usePRFileArtboards()
  const sections = usePRFileSections()
  const setPan = useSetPan()

  const [isPlacingDraftAnnotation] = usePlaceDraftAnnotation() || []
  const rootExtent = useRootNodeAbsoluteExtent()

  const artboardCoordinatesByUuid = useMemo(() => {
    const coordinatesByUuid: Record<string, ReferenceBounds> = {}

    artboards?.forEach(artboard => {
      coordinatesByUuid[artboard.objectId] = artboard.bounds
    })

    return coordinatesByUuid
  }, [artboards])

  const sectionCoordinatesByUuid = useMemo(() => {
    const coordinatesByUuid: Record<string, ReferenceBounds> = {}

    sections?.forEach(section => {
      coordinatesByUuid[section.objectId] = section.bounds
    })

    return coordinatesByUuid
  }, [sections])

  const getPrefixCoordinates = useCallback(
    (annotation: AnnotationDotFragment) => {
      if (annotation.currentSubject.__typename === 'Artboard') {
        return artboardCoordinatesByUuid[annotation.currentSubject.uuid]
      }

      if (annotation.sectionUuid) {
        return sectionCoordinatesByUuid[annotation.sectionUuid]
      }
    },
    [artboardCoordinatesByUuid, sectionCoordinatesByUuid]
  )

  const getArtboardContainerBounds = useFocusedFrameContainerBounds(
    artboardCoordinatesByUuid,
    otherProps.parentWrapper,
    cameraRef
  )

  const handleActiveAnnotation = useCallback(
    (annotation: AnnotationDotFragment) => {
      const { coordinates } = annotation

      if (!coordinates || !location.state?.isFromCommentTab) {
        return
      }

      const prefixBounds = getPrefixCoordinates(annotation)

      const adjustmentX = rootExtent.x - (prefixBounds?.x || 0)
      const adjustmentY = rootExtent.y - (prefixBounds?.y || 0)

      const panCoordinates = {
        x: -(coordinates.x - adjustmentX),
        y: -(coordinates.y - adjustmentY),
      }

      setPan({
        ...panCoordinates,
        centerPointInViewport: true,
      })
    },
    [getPrefixCoordinates, setPan, location, rootExtent]
  )

  return (
    <>
      {(activeAnnotationIdentifier || isPlacingDraftAnnotation) && (
        <PopoverFollowsPan />
      )}
      <AnnotationOverlay
        referenceBounds={rootExtent}
        getPrefixCoordinates={getPrefixCoordinates}
        getArtboardBounds={getPrefixCoordinates}
        getArtboardContainerBounds={getArtboardContainerBounds}
        onActiveAnnotation={handleActiveAnnotation}
        {...otherProps}
      />
    </>
  )
}

/**
 * When the user is panning on the canvas he might reach any
 * of the borders, if possible the popover should move to the
 * opposite side until reaching the other edge.
 *
 * Only pan: when zooming we already have the "DROPDOWN_MODIFIERS"
 * that keep the popover close to the dot.
 */
function PopoverFollowsPan() {
  const pan = usePan()

  useEffect(() => {
    throttleUpdatePopover()
  }, [pan?.x, pan?.y])

  return null
}

/**
 * Confines the annotation popover to the focused frame's rectangle on screen.
 *
 * Only meaningful in a frame focus view - on the page canvas, and in a Section
 * focus view, there is no single entity to confine it to and the popover is
 * free to sit anywhere over the canvas.
 *
 * Pan and zoom are read through a ref: the popover is only repositioned when
 * the camera settles, and reading them directly would rebuild this callback on
 * every frame of a drag.
 */
function useFocusedFrameContainerBounds(
  artboardCoordinatesByUuid: Record<string, ReferenceBounds>,
  parentWrapper: React.RefObject<HTMLElement | null>,
  cameraRef: React.RefObject<CameraSnapshot>
) {
  const scope = useCanvasScope()
  const focusedFrameUUID =
    scope?.focus.kind === 'frame' ? scope.focus.uuid : undefined

  const rootExtent = useRootNodeAbsoluteExtent()

  return useCallback((): ArtboardContainerBounds | undefined => {
    if (!focusedFrameUUID) return undefined

    const frameBounds = artboardCoordinatesByUuid[focusedFrameUUID]
    const { pan } = cameraRef.current
    const scale = cameraRef.current.zoom || 1

    if (!frameBounds || !rootExtent || !pan) return undefined
    if (!parentWrapper.current) return undefined

    const parentBounds = parentWrapper.current.getBoundingClientRect()

    const top = (pan.y + frameBounds.y - rootExtent.y) * scale + parentBounds.y
    const left = (pan.x + frameBounds.x - rootExtent.x) * scale + parentBounds.x

    return {
      top,
      bottom: top + frameBounds.height * scale,
      left,
      right: left + frameBounds.width * scale,
    }
  }, [
    focusedFrameUUID,
    artboardCoordinatesByUuid,
    rootExtent,
    parentWrapper,
    cameraRef,
  ])
}
